import React, { useState, useEffect, useMemo } from 'react';
import { apiCall, uploadFile, downloadFile } from '../utils/api';

const CATEGORIES = [
  { value: 'track-readiness', label: 'Track Readiness' },
  { value: 'strategy', label: 'Strategy & Growth' },
  { value: 'meeting-notes', label: 'Meeting Notes' },
  { value: 'decisions', label: 'Decisions & Minutes' },
  { value: 'other', label: 'Other' },
];

export default function FilesTab() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('track-readiness');
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(
    new Set(CATEGORIES.map((c) => c.value))
  );
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'size'
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [existingTags, setExistingTags] = useState(new Set());

  const loadFiles = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/files');
      setFiles(data);

      // Extract all existing tags
      const allTags = new Set();
      data.forEach((file) => {
        if (file.tags && Array.isArray(file.tags)) {
          file.tags.forEach((tag) => allTags.add(tag));
        }
      });
      setExistingTags(allTags);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadFiles();
  }, []);

  const filteredAndSortedFiles = useMemo(() => {
    let result = files
      .filter((f) => selectedCategories.has(f.category))
      .filter((f) => f.filename.toLowerCase().includes(searchTerm.toLowerCase()));

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.filename.localeCompare(b.filename);
        case 'size':
          return (b.size || 0) - (a.size || 0);
        case 'date':
        default:
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
      }
    });

    return result;
  }, [files, selectedCategories, searchTerm, sortBy]);

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const newFile = await uploadFile(selectedCategory, selectedFile);
      setFiles([newFile, ...files]);
      setSelectedFile(null);
      setError('');
      e.target.reset();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!confirm('Delete this file?')) return;

    try {
      await apiCall(`/api/files/${fileId}`, { method: 'DELETE' });
      setFiles(files.filter((f) => f.id !== fileId));
    } catch (err) {
      setError(err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const toggleCategory = (catValue) => {
    const newCats = new Set(selectedCategories);
    if (newCats.has(catValue)) {
      newCats.delete(catValue);
    } else {
      newCats.add(catValue);
    }
    setSelectedCategories(newCats);
  };

  const filesByCategory = CATEGORIES.map((cat) => ({
    category: cat,
    items: filteredAndSortedFiles.filter((f) => f.category === cat.value),
  })).filter(({ items }) => items.length > 0);

  if (loading)
    return <div className="flex items-center justify-center py-12">Loading files...</div>;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Upload Section */}
      <div className="card space-y-4 p-3 sm:p-4">
        <h3 className="text-base sm:text-lg font-bold font-headline">Upload a file</h3>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label htmlFor="category" className="block text-xs sm:text-sm font-medium mb-2">
                Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={uploading}
                className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="file" className="block text-xs sm:text-sm font-medium mb-2">
                Choose file
              </label>
              <input
                id="file"
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
                className="w-full px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg border border-outline-variant bg-surface text-on-surface disabled:opacity-50"
              />
            </div>
          </div>
          {selectedFile && (
            <p className="text-xs sm:text-sm text-on-surface-variant truncate">Selected: {selectedFile.name}</p>
          )}
          {error && <div className="error-message text-xs">{error}</div>}
          <button
            type="submit"
            disabled={uploading || !selectedFile}
            className="btn-primary disabled:opacity-50 w-full text-sm"
          >
            {uploading ? 'Uploading...' : 'Upload file'}
          </button>
        </form>
      </div>

      {/* Controls Section */}
      <div className="card space-y-4 p-3 sm:p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-xs sm:text-sm font-medium mb-2">
              Search files
            </label>
            <input
              id="search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by filename..."
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface placeholder-on-surface-variant focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Sort */}
          <div>
            <label htmlFor="sort" className="block text-xs sm:text-sm font-medium mb-2">
              Sort by
            </label>
            <select
              id="sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="date">Newest first</option>
              <option value="name">Name (A-Z)</option>
              <option value="size">Largest first</option>
            </select>
          </div>

          {/* Grouping Toggle */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Display</label>
            <button
              onClick={() => setGroupByCategory(!groupByCategory)}
              className={`w-full px-3 sm:px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                groupByCategory
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container text-on-surface hover:bg-surface-container-high'
              }`}
            >
              <span className="hidden sm:inline">{groupByCategory ? 'Grouped by Category' : 'Flat List'}</span>
              <span className="sm:hidden">{groupByCategory ? 'Grouped' : 'Flat'}</span>
            </button>
          </div>
        </div>

        {/* Category Filters */}
        <div>
          <label className="block text-xs sm:text-sm font-medium mb-2">Categories</label>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => toggleCategory(cat.value)}
                className={`badge text-xs transition-colors ${
                  selectedCategories.has(cat.value)
                    ? 'badge-primary'
                    : 'bg-surface-container text-on-surface hover:bg-surface-container-high'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Files List */}
      <div className="space-y-4 sm:space-y-6">
        {groupByCategory ? (
          // Grouped by category
          filesByCategory.map(({ category, items }) => (
            <div key={category.value} className="space-y-2 sm:space-y-3">
              <h3 className="font-bold font-headline text-base sm:text-lg text-primary">
                {category.label} ({items.length})
              </h3>
              <div className="space-y-2">
                {items.map((file) => (
                  <FileItem
                    key={file.id}
                    file={file}
                    onPreview={() => setPreviewFile(file)}
                    onDelete={handleDelete}
                    formatFileSize={formatFileSize}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          // Flat list
          <div className="space-y-2">
            {filteredAndSortedFiles.map((file) => (
              <FileItem
                key={file.id}
                file={file}
                onPreview={() => setPreviewFile(file)}
                onDelete={handleDelete}
                formatFileSize={formatFileSize}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}

        {filteredAndSortedFiles.length === 0 && (
          <div className="card text-center py-8 text-on-surface-variant">
            <p>No files match your search. Upload documents, track plans, and strategy notes.</p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          formatFileSize={formatFileSize}
          formatDate={formatDate}
          onDelete={handleDelete}
          existingTags={Array.from(existingTags).sort()}
        />
      )}
    </div>
  );
}

function FileItem({ file, onPreview, onDelete, formatFileSize, formatDate }) {
  return (
    <div className="card flex flex-col gap-3 p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="flex-1 min-w-0">
          <button
            onClick={onPreview}
            className="text-primary hover:underline font-medium text-sm sm:text-base truncate text-left"
          >
            {file.filename}
          </button>
          <p className="text-xs text-on-surface-variant mt-1">
            {formatFileSize(file.size)} • {formatDate(file.uploaded_at)} • {file.uploaded_by}
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-shrink-0">
          <button
            onClick={() => downloadFile(file.id, file.filename)}
            className="badge badge-primary hover:opacity-80 text-xs flex-1 sm:flex-none text-center cursor-pointer"
          >
            Download
          </button>
          <button
            onClick={() => {
              if (confirm('Delete this file?')) onDelete(file.id);
            }}
            className="badge bg-error text-on-error hover:opacity-80 text-xs"
          >
            Delete
          </button>
        </div>
      </div>
      {file.tags && file.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {file.tags.map((tag) => (
            <span key={tag} className="badge text-xs bg-surface-container text-on-surface">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FilePreviewModal({ file, onClose, formatFileSize, formatDate, onDelete, existingTags }) {
  const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(file.filename);
  const isPdf = /\.pdf$/i.test(file.filename);
  const [tags, setTags] = useState(file.tags || []);
  const [selectedTag, setSelectedTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [error, setError] = useState('');

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!selectedTag) return;

    setAddingTag(true);
    setError('');

    try {
      await apiCall(`/api/files/${file.id}/tags`, {
        method: 'POST',
        body: JSON.stringify({ tag: selectedTag }),
      });
      setTags([...tags, selectedTag].sort());
      setSelectedTag('');
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tag) => {
    try {
      await apiCall(`/api/files/${file.id}/tags/${encodeURIComponent(tag)}`, {
        method: 'DELETE',
      });
      setTags(tags.filter((t) => t !== tag));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-container-lowest rounded-lg sm:rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between p-3 sm:p-6 border-b border-outline-variant gap-3">
          <h2 className="font-bold font-headline text-sm sm:text-lg truncate">{file.filename}</h2>
          <button
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface font-bold text-2xl flex-shrink-0"
          >
            ×
          </button>
        </div>

        <div className="p-3 sm:p-6">
          {isImage && (
            <img src={getDownloadUrl(file.id)} alt={file.filename} className="w-full rounded-lg" />
          )}
          {isPdf && (
            <div className="bg-surface-container p-4 rounded-lg text-center">
              <p className="text-on-surface-variant mb-4">PDF Preview</p>
              <a
                href={getDownloadUrl(file.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-block"
              >
                Open PDF
              </a>
            </div>
          )}
          {!isImage && !isPdf && (
            <div className="bg-surface-container p-3 sm:p-6 rounded-lg text-center">
              <p className="text-xs sm:text-base text-on-surface-variant mb-4">
                Preview not available for this file type
              </p>
              <button
                onClick={() => downloadFile(file.id, file.filename)}
                className="btn-primary inline-block text-sm cursor-pointer"
              >
                Download file
              </button>
            </div>
          )}

          <div className="mt-3 sm:mt-6 pt-3 sm:pt-6 border-t border-outline-variant space-y-2">
            <p className="text-xs sm:text-sm">
              <strong>Size:</strong> {formatFileSize(file.size)}
            </p>
            <p className="text-xs sm:text-sm">
              <strong>Uploaded:</strong> {formatDate(file.uploaded_at)} by {file.uploaded_by}
            </p>
            <p className="text-xs sm:text-sm">
              <strong>Category:</strong> {file.category}
            </p>
          </div>

          <div className="mt-3 sm:mt-6 pt-3 sm:pt-6 border-t border-outline-variant space-y-3">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-on-surface-variant mb-2">
                Tags ({tags.length})
              </label>
              {tags.length > 0 && (
                <div className="mb-3 p-3 bg-surface-container rounded-lg">
                  <p className="text-xs font-semibold text-on-surface-variant mb-2">File Tags:</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <div key={tag} className="badge bg-primary text-on-primary flex items-center gap-2 text-xs px-3 py-2">
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:opacity-70 font-bold ml-1 text-lg leading-none"
                          title="Remove tag"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <form onSubmit={handleAddTag} className="space-y-2">
                <div>
                  <label className="block text-xs font-medium mb-1 text-on-surface-variant">Add tag from existing tags:</label>
                  <select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                    disabled={addingTag}
                    className="w-full px-3 py-2 text-xs rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  >
                    <option value="">-- Select a tag --</option>
                    {existingTags?.filter(tag => !tags.includes(tag)).map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={addingTag || !selectedTag}
                  className="w-full badge badge-primary hover:opacity-80 text-xs disabled:opacity-50 py-2"
                >
                  {addingTag ? 'Adding...' : 'Add Tag'}
                </button>
              </form>
              {error && <p className="text-xs text-error mt-2">{error}</p>}
            </div>
          </div>

          <div className="mt-3 sm:mt-6 flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => downloadFile(file.id, file.filename)}
              className="btn-primary flex-1 text-center text-sm cursor-pointer"
            >
              Download
            </button>
            <button
              onClick={() => {
                if (confirm('Delete this file?')) {
                  onDelete(file.id);
                  onClose();
                }
              }}
              className="btn-danger text-sm w-full sm:w-auto"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
